/**
 * TrainingProfileSelector Component
 * Provides a compact learner-facing trigger that opens a searchable modal for
 * published heat pump training profiles.
 *
 * @module TrainingProfileSelector
 */

import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Breadcrumb,
  Button,
  Empty,
  Input,
  Modal,
  Select,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  BookOutlined,
  ClearOutlined,
  CloseOutlined,
  ExperimentOutlined,
  GlobalOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { getFileUrl } from '../../../utils/urlUtils';
import styles from './TrainingProfileSelector.module.css';

const { Text } = Typography;

const buildLanguageLabel = (profile) => {
  return profile.countryLanguage || 'General';
};

/**
 * Select published training profiles for profile-enabled simulation mode.
 * @param {Object} props - Component props
 * @returns {JSX.Element}
 */
export const TrainingProfileSelector = ({
  profiles,
  selectedProfileId,
  selectedProfile,
  loading,
  error,
  onProfileChange,
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [nameQuery, setNameQuery] = useState('');
  const [languageFilter, setLanguageFilter] = useState(undefined);
  const [previewProfileId, setPreviewProfileId] = useState(null);

  const languageOptions = useMemo(() => (
    [...new Set(
      profiles
        .map((profile) => buildLanguageLabel(profile))
        .filter(Boolean)
    )]
      .sort((left, right) => left.localeCompare(right))
      .map((value) => ({ value, label: value }))
  ), [profiles]);

  const filteredProfiles = useMemo(() => {
    const normalizedQuery = nameQuery.trim().toLowerCase();

    return [...profiles]
      .filter((profile) => {
        const languageLabel = buildLanguageLabel(profile);
        const matchesLanguage = !languageFilter || languageLabel === languageFilter;
        const matchesName = !normalizedQuery || profile.name.toLowerCase().includes(normalizedQuery);
        return matchesLanguage && matchesName;
      })
      .sort((left, right) => {
        const leftLanguage = buildLanguageLabel(left);
        const rightLanguage = buildLanguageLabel(right);
        const languageCompare = leftLanguage.localeCompare(rightLanguage);
        if (languageCompare !== 0) {
          return languageCompare;
        }

        return left.name.localeCompare(right.name);
      });
  }, [languageFilter, nameQuery, profiles]);

  const activeProfile = useMemo(() => {
    if (selectedProfile) {
      return selectedProfile;
    }

    return profiles.find((profile) => profile.id === selectedProfileId) || null;
  }, [profiles, selectedProfile, selectedProfileId]);

  const previewProfile = useMemo(() => {
    if (previewProfileId == null) {
      return null;
    }

    return filteredProfiles.find((profile) => profile.id === previewProfileId)
      || profiles.find((profile) => profile.id === previewProfileId)
      || null;
  }, [filteredProfiles, previewProfileId, profiles]);

  const activeLanguageLabel = activeProfile ? buildLanguageLabel(activeProfile) : '';

  useEffect(() => {
    if (!isModalOpen) {
      setPreviewProfileId(null);
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (!previewProfileId) {
      return;
    }

    const profileStillExists = filteredProfiles.some((profile) => profile.id === previewProfileId)
      || profiles.some((profile) => profile.id === previewProfileId);

    if (!profileStillExists) {
      setPreviewProfileId(null);
    }
  }, [filteredProfiles, previewProfileId, profiles]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setPreviewProfileId(null);
  };

  const handleOpenPreview = (profileId) => {
    setPreviewProfileId(profileId);
  };

  const handleSelectProfile = (profileId) => {
    onProfileChange(profileId);
    setIsModalOpen(false);
    setPreviewProfileId(null);
  };

  return (
    <>
      <div className={styles.triggerWrap}>
        <button
          type="button"
          className={styles.triggerButton}
          onClick={() => setIsModalOpen(true)}
        >
          <div className={styles.triggerLead}>
            <span className={styles.triggerIconWrap}>
              <ExperimentOutlined />
            </span>
            <span className={styles.triggerButtonText}>
              {activeProfile
                ? t('heatPumpSimulation.trainingProfileLabel', 'Training profile:')
                : t('heatPumpSimulation.chooseTrainingProfile', 'Choose training profile')}
            </span>
          </div>

          <div className={styles.triggerMetaArea}>
            {activeProfile ? (
              <div className={styles.selectedProfilePanel}>
                <span className={styles.selectedProfileName}>{activeProfile.name}</span>
                {activeLanguageLabel && (
                  <span className={styles.selectedProfileLanguage}>({activeLanguageLabel})</span>
                )}
                <div
                  className={styles.inlineClearBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    onProfileChange(null);
                  }}
                  title={t('heatPumpSimulation.clearTrainingProfile', 'Clear profile')}
                >
                  <CloseOutlined />
                </div>
              </div>
            ) : null}
            {loading && <Spin size="small" />}
          </div>
        </button>
      </div>

      <Modal
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={920}
        title={(
          <div className={styles.modalTitleWrap}>
            <ExperimentOutlined className={styles.modalTitleIcon} />
            <div>
              <div className={styles.modalTitle}>{t('heatPumpSimulation.selectTrainingProfile', 'Select a Training Profile')}</div>
              <div className={styles.modalSubtitle}>{t('heatPumpSimulation.trainingProfileModalSubtitle', 'Browse profiles by language and name, then apply the one that matches your learning path.')}</div>
            </div>
          </div>
        )}
      >
        <div className={styles.modalContent}>
          {!previewProfile && (
            <div className={styles.filtersRow}>
              <Input
                allowClear
                value={nameQuery}
                onChange={(event) => setNameQuery(event.target.value)}
                prefix={<SearchOutlined />}
                placeholder={t('common.searchByName', 'Search by name')}
                className={styles.filterControl}
              />
              <Select
                allowClear
                value={languageFilter}
                onChange={setLanguageFilter}
                placeholder={t('common.filterByLanguage', 'Filter by language')}
                options={languageOptions}
                className={styles.filterControl}
              />
              <Button
                className={styles.clearButton}
                icon={<ClearOutlined />}
                disabled={!selectedProfileId}
                onClick={() => onProfileChange(null)}
              >
                {t('heatPumpSimulation.clearTrainingProfile', 'Clear profile')}
              </Button>
            </div>
          )}

          {error && (
            <Alert
              type="warning"
              showIcon
              message={t('heatPumpSimulation.trainingProfilesLoadFailed', 'Training profile list could not be loaded')}
              description={error.message || t('common.pleaseTryAgain', 'Please try again.')}
            />
          )}

          {!previewProfile && filteredProfiles.length === 0 ? (
            <div className={styles.emptyState}>
              <Empty description={t('heatPumpSimulation.noTrainingProfilesMatchFilters', 'No training profiles match the current filters')} />
            </div>
          ) : previewProfile ? (
            <div className={styles.previewPanel}>
              <div className={styles.previewHeaderRow}>
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  className={styles.backButton}
                  onClick={() => setPreviewProfileId(null)}
                >
                  {t('common.back', 'Back')}
                </Button>
                <Breadcrumb
                  items={[
                    { title: t('heatPumpSimulation.selectTrainingProfile', 'Select a Training Profile') },
                    { title: previewProfile.name },
                  ]}
                />
              </div>

              <div className={`${styles.profileCard} ${styles.profilePreviewCard} ${previewProfile.id === selectedProfileId ? styles.profileCardSelected : ''}`}>
                <div className={styles.profileCardHeader}>
                  <div className={styles.profileIdentity}>
                    {previewProfile.countryFlagImageUrl ? (
                      <img src={getFileUrl(previewProfile.countryFlagImageUrl)} alt="" className={styles.flagImage} />
                    ) : (
                      <div className={styles.flagFallback}><GlobalOutlined /></div>
                    )}
                    <div>
                      <div className={styles.profileName}>{previewProfile.name}</div>
                      <div className={styles.profileLanguage}>{buildLanguageLabel(previewProfile)}</div>
                    </div>
                  </div>
                  {previewProfile.id === selectedProfileId && <Tag color="blue">{t('common.selected', 'Selected')}</Tag>}
                </div>

                <div className={styles.profileMetaTags}>
                  <Tag icon={<BookOutlined />} color="green">{previewProfile.tutorialQuestCount || 0} {t('trainingProfiles.quests', 'quests')}</Tag>
                  <Tag color="purple">{previewProfile.quizCollectionCount || 0} {t('trainingProfiles.quizCollections', 'quiz collections')}</Tag>
                  {previewProfile.additionalTextRichContent && <Tag color="gold">{t('heatPumpSimulation.guidanceIncluded', 'Guidance included')}</Tag>}
                </div>

                {previewProfile.additionalTextRichContent ? (
                  <div
                    className={styles.profileDescription}
                    dangerouslySetInnerHTML={{ __html: previewProfile.additionalTextRichContent }}
                  />
                ) : (
                  <Text type="secondary" className={styles.profileDescriptionFallback}>
                    {t('heatPumpSimulation.noTrainingProfileGuidance', 'No additional learner guidance is attached to this profile.')}
                  </Text>
                )}

                <div className={styles.previewActions}>
                  <Button onClick={() => setPreviewProfileId(null)}>
                    {t('common.back', 'Back')}
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => handleSelectProfile(previewProfile.id)}
                    disabled={previewProfile.id === selectedProfileId}
                  >
                    {previewProfile.id === selectedProfileId
                      ? t('common.selected', 'Selected')
                      : t('common.select', 'Select')}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.profileGrid}>
              {filteredProfiles.map((profile) => {
                const isSelected = profile.id === selectedProfileId;
                const languageLabel = buildLanguageLabel(profile);

                return (
                  <button
                    key={profile.id}
                    type="button"
                    className={`${styles.profileCard} ${isSelected ? styles.profileCardSelected : ''}`}
                    onClick={() => handleOpenPreview(profile.id)}
                  >
                    <div className={styles.profileCardHeader}>
                      <div className={styles.profileIdentity}>
                        {profile.countryFlagImageUrl ? (
                          <img src={getFileUrl(profile.countryFlagImageUrl)} alt="" className={styles.flagImage} />
                        ) : (
                          <div className={styles.flagFallback}><GlobalOutlined /></div>
                        )}
                        <div>
                          <div className={styles.profileName}>{profile.name}</div>
                          <div className={styles.profileLanguage}>{languageLabel}</div>
                        </div>
                      </div>
                      {isSelected && <Tag color="blue">{t('common.selected', 'Selected')}</Tag>}
                    </div>

                    <div className={styles.profileMetaTags}>
                      <Tag icon={<BookOutlined />} color="green">{profile.tutorialQuestCount || 0} {t('trainingProfiles.quests', 'quests')}</Tag>
                      <Tag color="purple">{profile.quizCollectionCount || 0} {t('trainingProfiles.quizCollections', 'quiz collections')}</Tag>
                      {profile.additionalTextRichContent && <Tag color="gold">{t('heatPumpSimulation.guidanceIncluded', 'Guidance included')}</Tag>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

TrainingProfileSelector.propTypes = {
  profiles: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    countryName: PropTypes.string,
    countryLanguage: PropTypes.string,
    countryFlagImageUrl: PropTypes.string,
    tutorialQuestCount: PropTypes.number,
    quizCollectionCount: PropTypes.number,
    additionalTextRichContent: PropTypes.string,
  })).isRequired,
  selectedProfileId: PropTypes.number,
  selectedProfile: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    countryName: PropTypes.string,
    countryLanguage: PropTypes.string,
  }),
  loading: PropTypes.bool,
  error: PropTypes.object,
  onProfileChange: PropTypes.func.isRequired,
};

TrainingProfileSelector.defaultProps = {
  selectedProfileId: null,
  selectedProfile: null,
  loading: false,
  error: null,
};

export default TrainingProfileSelector;
